// src/components/QRPrintModal.js
import { useState, useMemo } from 'react'
import QRCode from 'qrcode'
import { C } from '../theme'

export default function QRPrintModal({ products, onClose }) {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(() => new Set(products.map(p => p.id)))
  const [printing, setPrinting] = useState(false)

  const filtered = useMemo(() =>
    products.filter(p => !search || p.name.includes(search) || (p.sku || '').toLowerCase().includes(search.toLowerCase()))
  , [products, search])

  const selectedCount = selected.size

  const toggleAll = () => {
    setSelected(prev => prev.size === products.length ? new Set() : new Set(products.map(p => p.id)))
  }

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }

  const handlePrint = async () => {
    const toPrint = products.filter(p => selected.has(p.id))
    if (!toPrint.length) return
    setPrinting(true)

    // 列印時才產生 QR code
    const qrPairs = await Promise.all(
      toPrint.map(p =>
        QRCode.toDataURL(`${window.location.origin}?scan=in&pid=${p.id}`, { width: 200, margin: 1 })
          .then(url => [p.id, url])
      )
    )
    const qrMap = Object.fromEntries(qrPairs)

    const labelsHtml = toPrint.map(p => `
      <div class="label">
        <img src="${qrMap[p.id]}" width="120" height="120" />
        <div class="name">${p.name}</div>
        <div class="sub">SKU: ${p.sku || '—'} · 單位: ${p.unit || '—'}</div>
        ${p.category ? `<div class="cat">${p.category}</div>` : ''}
      </div>
    `).join('')

    const win = window.open('', '_blank')
    win.document.write(`
      <!DOCTYPE html><html><head><meta charset="utf-8"><title>商品 QR code 標籤</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: system-ui, sans-serif; }
        .grid { display: flex; flex-wrap: wrap; gap: 8px; padding: 12px; }
        .label { width: 160px; border: 1.5px solid #ccc; border-radius: 8px; padding: 10px 8px; text-align: center; page-break-inside: avoid; }
        img { display: block; margin: 0 auto 6px; }
        .name { font-size: 12px; font-weight: 700; line-height: 1.3; color: #111; margin-bottom: 3px; }
        .sub { font-size: 10px; color: #666; margin-bottom: 2px; }
        .cat { font-size: 10px; color: #7c3aed; font-weight: 600; }
        @media print { .grid { gap: 6px; padding: 8px; } }
      </style></head>
      <body><div class="grid">${labelsHtml}</div>
      <script>window.onload = () => { window.print(); window.onafterprint = () => window.close() }<\/script>
      </body></html>
    `)
    win.document.close()
    setPrinting(false)
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1500 }} />
      <div style={{ position: 'fixed', inset: 0, zIndex: 1501, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: C.bg, borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>

          {/* Header */}
          <div style={{ padding: '16px 20px', borderBottom: `1.5px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <span style={{ fontSize: 22 }}>🏷️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>列印 QR code 標籤</div>
              <div style={{ fontSize: 12, color: C.muted }}>選擇要列印的商品，貼在貨架上供員工掃描</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}>✕</button>
          </div>

          {/* Search + select all */}
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 10, flexShrink: 0 }}>
            <input
              style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.panel, color: C.text, fontSize: 13 }}
              placeholder="搜尋商品名稱 / SKU..."
              value={search} onChange={e => setSearch(e.target.value)}
            />
            <button onClick={toggleAll} style={{ padding: '8px 14px', borderRadius: 8, background: 'none', border: `1.5px solid ${C.border}`, color: C.muted, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {selected.size === products.length ? '全部取消' : '全選'}
            </button>
          </div>

          {/* Product list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
            {filtered.map(p => (
              <div key={p.id} onClick={() => toggle(p.id)} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                borderRadius: 10, cursor: 'pointer', marginBottom: 4,
                background: selected.has(p.id) ? C.accentDim : 'transparent',
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                  border: `2px solid ${selected.has(p.id) ? C.accent : C.border}`,
                  background: selected.has(p.id) ? C.accent : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {selected.has(p.id) && <span style={{ color: '#fff', fontSize: 13, fontWeight: 900 }}>✓</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>SKU: {p.sku || '—'} · 庫存: {p.stock} {p.unit}</div>
                </div>
                {p.category && (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#ede9fe', color: '#7c3aed', fontWeight: 700, flexShrink: 0 }}>{p.category}</span>
                )}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 13 }}>找不到符合的商品</div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 16px', borderTop: `1.5px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <div style={{ fontSize: 13, color: C.muted, flex: 1 }}>
              已選 <strong style={{ color: C.text }}>{selectedCount}</strong> 項商品
            </div>
            <button onClick={onClose} style={{ padding: '10px 16px', borderRadius: 10, background: 'none', border: `1.5px solid ${C.border}`, color: C.muted, fontSize: 13, cursor: 'pointer' }}>取消</button>
            <button
              onClick={handlePrint}
              disabled={printing || selectedCount === 0}
              style={{
                padding: '10px 20px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700,
                background: !printing && selectedCount > 0 ? C.accent : C.border,
                color: !printing && selectedCount > 0 ? '#fff' : C.muted,
                cursor: !printing && selectedCount > 0 ? 'pointer' : 'not-allowed',
              }}>
              {printing ? '產生中...' : '🖨️ 列印標籤'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
